import { z } from 'zod';
/**
 * The regression watch (Warren, 29 Oct 2026, item 7: "Keep it fixed: a daily check that compares each site's starts,
 * completions and energy per session with its own pre-cutover baseline and opens an anomaly (and runs the ladder) when
 * one drops"). Shared by the Api (lib/regressionWatch) and Admin (Settings, the anomalies page), so both read one set of
 * signals, words and defaults.
 *
 * Five signals per site, each against the site's own baseline:
 *   starts_per_day      start attempts a day (app and card; staff starts never count). Worse is lower.
 *   completion_rate     of the counted start attempts, the share that became a session that ended normally. Worse is lower.
 *   never_started_rate  of the counted start attempts, the share that failed to start. Worse is higher.
 *   cut_rate            of the sessions that ended, the share cut mid-charge (a failure by the session success rules).
 *                       Worse is higher.
 *   energy_per_session  the mean kWh of a session that charged, whether it ended normally or was cut mid-charge (0 kWh
 *                       rows left out), as v1's history counts every session that charged: a session that ends early
 *                       draws less, so this is the signal a cut shows on at a site v1 served. Worse is lower.
 * "Counted" is the session success rules' own (lib/sessionOutcomes): a start that started or failed; the driver's side,
 * access rules and staff starts are outside it, and so is every failure in our own roll or crash (NETWORK_FOLD_CAUSES).
 *
 * The baseline: the site's 1 to 21 Oct 2026 (its own days, in its own zone), stored once and editable in Admin.
 * Warren's later ruling keeps it permanently; subsequent v3 performance never replaces it.
 *
 * The windows (review, 29 Oct 2026): each check judges the trailing 1 to 7 days, 14 days and 28 days, all ending
 * yesterday. A total outage at a busy site is plain in 2 days, and a quiet site's only over weeks, so one fixed window
 * either misses the quiet sites or is slow at the busy ones. The chance a check allows (max_chance) is split evenly
 * across the windows it judges, so judging more windows never opens more anomalies on noise.
 */
export const REGRESSION_SIGNALS = ['starts_per_day', 'completion_rate', 'never_started_rate', 'cut_rate', 'energy_per_session'];
/** Which way is worse: 'down' a drop (starts, completion, energy), 'up' a rise (never started, cut). */
export const REGRESSION_WORSE = {
    starts_per_day: 'down', completion_rate: 'down', never_started_rate: 'up', cut_rate: 'up', energy_per_session: 'down',
};
/** A rate is a share of 1 and its threshold is in points of it; starts and energy are amounts and theirs is a share of the baseline. */
export const REGRESSION_IS_RATE = {
    starts_per_day: false, completion_rate: true, never_started_rate: true, cut_rate: true, energy_per_session: false,
};
export const REGRESSION_SIGNAL_LABELS = {
    starts_per_day: 'Start attempts per day',
    completion_rate: 'Completion rate',
    never_started_rate: 'Never-started rate',
    cut_rate: 'Cut mid-charge rate',
    energy_per_session: 'Energy per session',
};
/** What each signal's minimum volume counts, in one window. */
export const REGRESSION_VOLUME_LABELS = {
    starts_per_day: 'starts the baseline expects',
    completion_rate: 'counted start attempts',
    never_started_rate: 'counted start attempts',
    cut_rate: 'sessions that ended',
    energy_per_session: 'sessions that charged',
};
/** The pre-cutover window: 1 to 21 Oct 2026, both days included, in each site's own day. */
export const PRE_CUTOVER_FIRST_DAY = '2026-10-01';
export const PRE_CUTOVER_LAST_DAY = '2026-10-21';
export const PRE_CUTOVER_DAYS = 21;
/** Historical rolling-window constants retained for existing callers and old anomaly evidence only. */
export const ROLLING_BASELINE_DAYS = 28;
export const ROLLING_AFTER_V3_DAYS = 60;
/**
 * The windows each check judges, in days, each the trailing run ending yesterday. Against the pre-cutover baseline a
 * window never reaches back before CUTOVER_FIRST_DAY: one that would is cut to it, and one that is then the same as a
 * shorter one is judged once. Against the rolling baseline only those of 7 days or fewer are judged (a longer one would
 * overlap the 28 days it is compared with).
 */
export const REGRESSION_WINDOW_DAYS = [1, 2, 3, 4, 5, 6, 7, 14, 28];
/** The first day on v3 (the 22 Oct 2026 flip): the day after the pre-cutover window. */
export const CUTOVER_FIRST_DAY = '2026-10-22';
export const RegressionThresholdSchema = z.object({
    /** How much worse opens it: a share of the baseline for starts and energy (0.4 is 40% below), points for the rates (0.1 is 10 points). */
    change: z.number().min(0.01, 'At least 0.01.').max(0.9, 'At most 0.9.'),
    /** The fewest a window is judged on (REGRESSION_VOLUME_LABELS); a window below it is quiet: it neither opens nor closes anything. */
    min_volume: z.number().int('Use whole numbers.').min(1, 'At least 1.').max(10_000, 'At most 10000.'),
    /** The fewest a computed baseline must rest on to be used (an edited value is Warren's own and always used). */
    baseline_min: z.number().int('Use whole numbers.').min(1, 'At least 1.').max(100_000, 'At most 100000.'),
}).strict();
export const RegressionWatchSettingsSchema = z.object({
    /** On by default: the check only writes anomalies (the ladder it hands to keeps its own master switch). */
    enabled: z.boolean(),
    /** The site-local hour the daily check runs at or after (0 to 23). A site can have its own (its baseline row). */
    check_hour: z.number().int('Use a whole hour.').min(0, 'From 0.').max(23, 'To 23.'),
    /**
     * The chance noise alone opens an anomaly on one signal at one check: split evenly across the windows the check
     * judges, each window opens only when a drop that size is that unlikely by chance. It is per check, and a site is
     * checked every day, so at a site whose demand swings from week to week a few false openings a year remain.
     */
    max_chance: z.number().min(0.0001, 'At least 0.0001.').max(0.2, 'At most 0.2.'),
    /** Checks in a row with no window breaching and the week back within half the threshold before an open one closes. */
    clear_checks: z.number().int('Use whole numbers.').min(1, 'At least 1.').max(14, 'At most 14.'),
    thresholds: z.object(Object.fromEntries(REGRESSION_SIGNALS.map((s) => [s, RegressionThresholdSchema]))).strict(),
}).strict();
/**
 * The defaults (review, 29 Oct 2026: at this network's volumes 10 expected starts and 20 counted attempts left 12 of 17
 * public sites never judged). Starts need 5 expected in a window: below about 4.6 not even none at all is a 1% event,
 * so the chance rule governs, and the long windows give a quiet site that many; a starts baseline needs 5 sessions in
 * its 21 days (a site of 8 or 9 there is judged only on its long windows, where a drop must be near total). A rate
 * needs 10 in a window, so a rate set by hand at a site of 1 or 2 sessions a day is judged within a week or two.
 */
export const DEFAULT_REGRESSION_THRESHOLDS = {
    starts_per_day: { change: 0.4, min_volume: 5, baseline_min: 5 },
    completion_rate: { change: 0.1, min_volume: 10, baseline_min: 30 },
    never_started_rate: { change: 0.1, min_volume: 10, baseline_min: 30 },
    cut_rate: { change: 0.05, min_volume: 10, baseline_min: 30 },
    energy_per_session: { change: 0.25, min_volume: 10, baseline_min: 20 },
};
export const DEFAULT_REGRESSION_WATCH_SETTINGS = {
    enabled: true,
    check_hour: 6,
    max_chance: 0.01,
    clear_checks: 2,
    thresholds: Object.fromEntries(REGRESSION_SIGNALS.map((s) => [s, { ...DEFAULT_REGRESSION_THRESHOLDS[s] }])),
};
/** A saved row read with the defaults under it: anything missing or out of range is the default. */
export function regressionWatchSettingsOf(value) {
    const d = DEFAULT_REGRESSION_WATCH_SETTINGS;
    const v = value ?? {};
    const pick = (schema, raw, fallback) => { const r = schema.safeParse(raw); return r.success ? r.data : fallback; };
    const shape = RegressionWatchSettingsSchema.shape;
    const saved = (v['thresholds'] ?? {});
    return {
        // On unless a saved row says false.
        enabled: v['enabled'] !== false,
        check_hour: pick(shape.check_hour, v['check_hour'], d.check_hour),
        max_chance: pick(shape.max_chance, v['max_chance'], d.max_chance),
        clear_checks: pick(shape.clear_checks, v['clear_checks'], d.clear_checks),
        thresholds: Object.fromEntries(REGRESSION_SIGNALS.map((s) => {
            const row = (saved[s] ?? {});
            const t = RegressionThresholdSchema.shape;
            return [s, {
                    change: pick(t.change, row['change'], d.thresholds[s].change),
                    min_volume: pick(t.min_volume, row['min_volume'], d.thresholds[s].min_volume),
                    baseline_min: pick(t.baseline_min, row['baseline_min'], d.thresholds[s].baseline_min),
                }];
        })),
    };
}
/** One edit from Admin: a value per signal (null clears it), and the site's own check hour (null: the network's). */
export const RegressionBaselineEditSchema = z.object({
    metrics: z.object({
        starts_per_day: z.number().min(0, 'At least 0.').max(10_000, 'At most 10000 a day.').nullable(),
        completion_rate: z.number().min(0, 'From 0%.').max(1, 'To 100%.').nullable(),
        never_started_rate: z.number().min(0, 'From 0%.').max(1, 'To 100%.').nullable(),
        cut_rate: z.number().min(0, 'From 0%.').max(1, 'To 100%.').nullable(),
        energy_per_session: z.number().min(0, 'At least 0.').max(500, 'At most 500 kWh.').nullable(),
    }).partial().strict().optional(),
    check_hour: z.number().int('Use a whole hour.').min(0, 'From 0.').max(23, 'To 23.').nullable().optional(),
}).strict();
//# sourceMappingURL=regressionWatch.js.map